/**
 * One feedback message's conversation on the Desk, for editors: what Customer Service or an editor sent,
 * what the reader wrote back, and a reply box. The send is the Feedback inbox's own (title From address,
 * team sign-off, Emails Log, threading), so a reply here shows there too.
 */
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { describeError } from '../lib/describeError'
import { publicationName } from '../lib/format'
import { archiveFeedback, replyToFeedback, restoreFeedback, type DeskFeedbackItem } from './api'
import { isRepeatPrompt, replyState, replySubject, staffName } from './feedback'
import { etDate, etTime } from './labels'
import { btn, btnPrimary, btnSmall, errorText, field } from './piece/ui'

type Props = {
  m: DeskFeedbackItem
  publicationSlug: string
  /** The dialog asks before closing over an unsent reply. */
  onDirty: (id: number, dirty: boolean) => void
}

const when = (iso: string | null) => (iso ? `${etDate(iso)} · ${etTime(iso)}` : '')

export function FeedbackThread({ m, publicationSlug, onDirty }: Props) {
  const qc = useQueryClient()
  const state = replyState(m)
  const [open, setOpen] = useState(state === 'answered_back')
  const [writing, setWriting] = useState(false)
  const [subject, setSubject] = useState(replySubject(publicationName(publicationSlug)))
  const [body, setBody] = useState('')
  const [askRepeat, setAskRepeat] = useState(false)
  const [sent, setSent] = useState(false)
  const lastReply = m.replies.at(-1)

  const send = useMutation({
    mutationFn: (confirm: boolean) => replyToFeedback(publicationSlug, m.id, { subject, body_md: body, confirm_repeat: confirm }),
    onSuccess: () => {
      setWriting(false); setBody(''); setAskRepeat(false); setSent(true); setOpen(true)
      onDirty(m.id, false)
      refresh()
    },
    onError: (err) => { if (isRepeatPrompt(err)) setAskRepeat(true) },
  })

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['editorial-feedback', publicationSlug] })
    void qc.invalidateQueries({ queryKey: ['editorial-desk', publicationSlug] })
  }
  const file = useMutation({
    mutationFn: () => (m.archived ? restoreFeedback : archiveFeedback)(publicationSlug, m.id),
    onSuccess: refresh,
  })

  const setText = (v: string) => { setBody(v); onDirty(m.id, v.trim() !== '') }
  const cancel = () => { setWriting(false); setBody(''); setAskRepeat(false); send.reset(); onDirty(m.id, false) }
  const history = [
    ...m.replies.map((r) => ({ at: r.created_at, who: `Reply from ${staffName(r.sent_by)}`, text: r.body_md, ours: true })),
    ...m.answers.filter((a) => !a.auto_reply).map((a) => ({ at: a.received_at, who: `${a.from_name || 'The reader'} wrote back`, text: a.body_text ?? '', ours: false })),
  ].sort((a, b) => (a.at ?? '').localeCompare(b.at ?? ''))

  return (
    <div className="flex flex-col gap-2.5 border-t border-ink-500 pt-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[12px]">
        {state === 'hold' && (
          <span className="inline-flex items-center gap-2 rounded-full bg-amber-wash px-2.5 py-1 font-semibold text-amber-deep">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-deep" aria-hidden="true" />
            Customer Service is handling this one: it needs an account or billing change.
          </span>
        )}
        {(state === 'replied' || state === 'answered_back') && lastReply && (
          <span className="inline-flex items-center gap-2 font-semibold text-radar-green">
            <span className="h-1.5 w-1.5 rounded-full bg-radar-green" aria-hidden="true" />
            Replied {etDate(lastReply.created_at ?? '')} by {staffName(lastReply.sent_by)}
            {m.replies.length > 1 && <span className="font-normal text-paper-faint">({m.replies.length} replies)</span>}
          </span>
        )}
        {state === 'answered_back' && (
          <span className="inline-flex items-center gap-2 font-semibold text-radar-cyan">
            <span className="h-1.5 w-1.5 rounded-full bg-radar-cyan" aria-hidden="true" />
            The reader wrote back
          </span>
        )}
        {state === 'no_email' && <span className="text-paper-faint">No email left, so this one can’t be answered.</span>}
        {sent && <span role="status" className="font-semibold text-radar-green">Sent.</span>}
        {m.archived && m.archived_at && (
          <span className="text-paper-faint">Archived {etDate(m.archived_at)}{m.archived_by ? ` by ${staffName(m.archived_by)}` : ''}</span>
        )}
        {!m.archived && m.archived_at && (
          <span className="inline-flex items-center gap-2 font-semibold text-radar-cyan">Back from the archive: the reader wrote back</span>
        )}
        {file.isError && <span role="alert" className="text-radar-red">{describeError(file.error)}</span>}
        <span className="ml-auto flex flex-wrap gap-2">
          {history.length > 0 && (
            <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className={btnSmall}>
              {open ? 'Hide conversation' : 'Show conversation'}
            </button>
          )}
          {m.can_reply && !writing && (
            <button type="button" onClick={() => { setWriting(true); setSent(false) }} className={btnSmall}>
              {m.replies.length > 0 ? 'Reply again' : 'Reply'}
            </button>
          )}
          {!writing && (
            <button type="button" onClick={() => file.mutate()} disabled={file.isPending} className={btnSmall}>
              {m.archived ? 'Restore' : 'Archive'}
            </button>
          )}
        </span>
      </div>

      {open && history.length > 0 && (
        <ol className="m-0 flex list-none flex-col gap-2 p-0">
          {history.map((h, i) => (
            <li key={i} className={`flex flex-col gap-1 rounded-lg px-3.5 py-2.5 ${h.ours ? 'ml-6 bg-ink-600' : 'mr-6 border border-ink-500'}`}>
              <span className="text-[12px] font-semibold text-paper-dim">{h.who} <span className="font-normal text-paper-faint">· {when(h.at)}</span></span>
              <p className="m-0 whitespace-pre-wrap break-words text-[14px] leading-relaxed text-paper">{h.text}</p>
            </li>
          ))}
        </ol>
      )}

      {writing && (
        <form className="flex flex-col gap-2.5 rounded-xl border border-ink-500 bg-ink-700 p-3.5"
              onKeyDown={(e) => { if (e.key === 'Escape') e.stopPropagation() }}
              onSubmit={(e) => { e.preventDefault(); send.mutate(false) }}>
          {lastReply && (
            <p className="m-0 rounded-lg bg-amber-wash px-3 py-2 text-[13px] text-amber-deep">
              Already answered by {staffName(lastReply.sent_by)} on {etDate(lastReply.created_at ?? '')}. This sends another email.
            </p>
          )}
          <label className="flex flex-col gap-1 text-[12px] font-semibold text-paper-dim">
            Subject
            <input className={field} value={subject} maxLength={255} onChange={(e) => setSubject(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-semibold text-paper-dim">
            Your reply to {m.member_name || m.email}
            <textarea className={`${field} min-h-[140px] resize-y leading-relaxed`} value={body} autoFocus
                      onChange={(e) => setText(e.target.value)} placeholder="Write to the reader…" />
          </label>
          <p className="m-0 text-[12px] text-paper-faint">
            Goes from service@airage.com in the title’s look and ends with the team sign-off. Their answer comes back to Customer Service.
          </p>
          {send.isError && !askRepeat && <p role="alert" className={errorText}>{describeError(send.error)}</p>}
          {askRepeat ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[13px] text-amber-deep">A reply went to this person in the last 10 minutes. Send another?</span>
              <button type="button" className={btnPrimary} disabled={send.isPending} onClick={() => send.mutate(true)}>Send anyway</button>
              <button type="button" className={btn} onClick={cancel}>Cancel</button>
            </div>
          ) : (
            <div className="flex flex-wrap justify-end gap-2">
              <button type="button" className={btn} onClick={cancel}>Cancel</button>
              <button type="submit" className={btnPrimary} disabled={send.isPending || !body.trim() || !subject.trim()}>
                {send.isPending ? 'Sending…' : 'Send reply'}
              </button>
            </div>
          )}
        </form>
      )}
    </div>
  )
}
